import React from "react";
import { Modal, Button, Row, Col, Card } from "react-bootstrap";
import { X, Calendar, Clock, MapPin, FileText, User } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { returnPhysicalAddress } from "../../utilities/hybridSales";
import { SheriffCards } from "./SheriffCards";
import { reverseSaleTypeMapping } from "../utils";

function formatLongDate(mmddyyyy) {
  const [month, day, year] = mmddyyyy?.split("/");
  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function DetailRow({ icon, label, value }) {
  return (
    <div className="ts-row mb-4">
      <div className="d-flex align-items-center gap-2">
        {icon}
        <span className="fw-medium text-dark">{label}</span>
      </div>

      <div>
        <div className="ts-value">{value}</div>
      </div>
    </div>
  );
}

function ContactCard({ role, name, phone }) {
  const tel = phone?.replace(/[^\d+]/g, "");
  return (
    <Card className="h-100 w-100" style={{ border: "none" }}>
      <Card.Body className="d-flex gap-3">
        <User size={22} className="orange" />
        <div>
          <div className="text-secondary fw-medium">{role}</div>
          <div className="fs-8">{name}</div>
          {phone && (
            <a href={`tel:${tel}`} className="text-decoration-none orange">
              {phone}
            </a>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}

// const SheriffCard = ({ name, phone }) => {
//   const tel = phone?.replace(/[^\d+]/g, "");
//   return (
//     <Card className="h-100 w-100" style={{ border: "none" }}>
//       <Card.Body className="d-flex gap-3">
//         <User size={22} className="orange" />
//         <div>
//           <div className="text-secondary fw-medium">Sheriff</div>
//           <div className="fs-8">{name}</div>
//           {phone && (
//             <a href={`tel:${tel}`} className="text-decoration-none orange">
//               {phone}
//             </a>
//           )}
//         </div>
//       </Card.Body>
//     </Card>
//   );
// };

export default function TaxSaleDetailsModal({ onHide, sale, contacts, saleTypeParam, sheriffDetails, tsmDetails }) {
  const { stateCode, countyName } = useParams();
  const navigate = useNavigate();

  if (!sale) return null;

  const valueToDisplay = (value) => {
    if (!value) return "Data not available";
    return value;
  };

  const navigateToProperty = (toMap) => {
    // properties?state=IN&county=Carroll&saleType=foreclosure&timeFrame=Past+4+Weeks
    let baseUrl = `/properties?state=${stateCode}&county=${countyName}&saleType=${saleTypeParam}`;
    if (toMap) {
      baseUrl += `&openMap=true`;
      navigate(baseUrl);
    } else {
      navigate(baseUrl);
    }
  };

  const casedSaleType = reverseSaleTypeMapping[sale.SaleType]?.charAt(0).toUpperCase() + reverseSaleTypeMapping[sale.SaleType]?.slice(1) || "Unknown";

  return (
    <Modal
      show={!!sale}
      onHide={onHide}
      centered
      size="md"
      dialogClassName="ts-modal"
      contentClassName="ts-modal-content"
    >
      <Modal.Header className="ts-header mb-0 pb-0" style={{ borderBottom: 0 }} closeButton onHide={onHide}>
        <div>
          <Modal.Title className="ts-title h5">{casedSaleType} Sale Details – {sale.Date}</Modal.Title>
          <div className="ts-subtitle text-muted fs-8">Complete information about this sale</div>
        </div>
      </Modal.Header>

      <Modal.Body className="ts-body mt-0">
        <div className="ts-section-title fs-8 mb-2">Sale Details</div>

        <DetailRow
          icon={<Calendar size={22} className="orange" />}
          label="Sale Date"
          value={valueToDisplay(formatLongDate(sale.Date))}
        />

        <DetailRow icon={<Clock size={22} className="orange" />} label="Sale Hours" value={valueToDisplay(sale.Time)} />

        <DetailRow
          icon={<Calendar size={22} className="orange" />}
          label="Registration Period"
          value={valueToDisplay(sale.RegistrationPeriod)}
        />

        <DetailRow
          icon={<MapPin size={22} className="orange" />}
          label="Sale Location"
          value={valueToDisplay(returnPhysicalAddress(sale.Location))}
        />

        <DetailRow
          icon={<FileText size={22} className="orange" />}
          label="Cause Number"
          value={valueToDisplay(sale.CauseNumber)}
        />

        <div className="ts-section-title fs-8 mb-2">Contact Information</div>
        <Row className="g-4 mb-5">
          {sale.saleType === "F" && sheriffDetails?.SheriffName ? (
            <SheriffCards
              name={sheriffDetails?.SheriffName}
              phone={sheriffDetails?.SheriffPhone}
              foreclosureLabel={tsmDetails?.ContactPhone3Label}
              foreclosurePhone={tsmDetails?.ContactPhone3Value}
            />
          ) : contacts.length > 0 ? (
            contacts.map((c) => (
              <Col key={c.role} md={12}>
                <ContactCard role={c.role} name={c.name} phone={c.phone} />
              </Col>
            ))
          ) : (
            <Col md={6}>
              <span>No contact information available</span>
            </Col>
          )}
        </Row>

        <div className="mt-0 pt-2 d-flex gap-3">
          <Button className="ts-btn viewDetailsBtn w-100 flex-fill" onClick={() => navigateToProperty(false)}>
            View Sale
          </Button>
          <Button
            className="ts-btn viewDetailsBtn w-100 flex-fill"
            onClick={() => {
              navigateToProperty(true);
            }}
          >
            View Map
          </Button>
        </div>
      </Modal.Body>
    </Modal>
  );
}
